<template>
  <div class="nr-table-container">
    <table
      v-if="rows.length"
      class="nr-table"
      :class="{ striped: striped }"
      ref="table"
    >
      <thead>
        <tr>
          <template v-for="(column, index) in parsedColumns">
            <slot name="column" v-bind="column">
              <th
                :class="{
                  sticky: column.sticky,
                  'is-sortable': column.sortable
                }"
                :key="`header-column-${index}`"
                :data-field="column.field"
              >
                <template v-if="column.sortable">
                  <div class="nr-table-sort-header">
                    {{ column.label }}
                    <NrIconButton
                      size="small"
                      variant="minimal-neutral"
                      :aria-label="`sort by ${column.label}`"
                      :icon="{ name: sortIcon(column.field) }"
                      @click="handleTableSort(column.field)"
                    />
                  </div>
                </template>
                <template v-else>
                  {{ column.label }}
                </template>
              </th>
            </slot>
          </template>
        </tr>
      </thead>

      <tbody>
        <tr v-for="(row, index) in parsedRows" :key="`data-row-${index}`">
          <slot name="row" v-bind:row="row">
            <template v-for="column in parsedColumns">
              <slot
                :name="column.field"
                :row="row"
                :value="row[column.field]"
                :field="column.field"
                :column="getColumnDataByField(column.field)"
                :data-key="`data-row-${index}-col-${column.field}`"
                :sticky="getColumnDataByField(column.field).sticky"
              >
                <component
                  :key="`data-row-${index}-col-${column.field}`"
                  :is="getColumnDataByField(column.field).sticky ? 'th' : 'td'"
                >
                  {{ row[column.field] }}
                </component>
              </slot>
            </template>
          </slot>
        </tr>
      </tbody>
    </table>
    <div v-if="!rows.length && !isLoading" class="nr-table-empty">
      <div v-if="emptyText">
        <p class="text">{{ emptyText }}</p>
      </div>
      <div v-else>
        <p class="text">No {{ tableName }} found.</p>
        <p>
          Try adjusting your filters to find the
          {{ tableName }} you are looking for.
        </p>
      </div>
    </div>
    <div v-if="isLoading" class="nr-table-loading">
      <div class="nr-table-overlay"></div>
      <NrIcon class="nr-table-spinner" name="spinner" />
    </div>
  </div>
</template>

<script lang="ts">
import { PropType } from 'vue'
import NrIcon from '../NrIcon/NrIcon.vue'
import NrIconButton from '../NrIconButton/nr-icon-button.vue'

interface TableHeader {
  label: string
  field: string
  hidden?: boolean
  sticky?: boolean
  sortable?: boolean
}

interface TableRow {
  [key: string]: any
}

export default {
  components: { NrIcon, NrIconButton },

  props: {
    striped: Boolean,
    isLoading: {
      type: Boolean,
      default: false
    },
    tableName: {
      type: String,
      default() {
        return 'data'
      }
    },
    columns: {
      type: Array as PropType<TableHeader[]>,
      default() {
        return []
      }
    },
    rows: {
      type: Array as PropType<TableRow[]>,
      default() {
        return []
      }
    },
    emptyText: {
      type: String,
      default() {
        return ''
      }
    }
  },

  data() {
    return {
      config: {}
    }
  },

  computed: {
    columnFields() {
      return this.parsedColumns.map((column) => column.field)
    },

    parsedColumns() {
      return this.columns.filter((column) => !column.hidden)
    },

    parsedRows() {
      return this.rows.map((row) => {
        return this.columnFields
          .map((field) => ({ [field]: row[field] }))
          .reduce((newRow, column) => Object.assign(newRow, column, row), {})
      })
    }
  },

  methods: {
    getColumnDataByField(field) {
      return this.parsedColumns.find((column) => field === column.field)
    },

    handleTableSort(field) {
      const order = this.config[field]?.order === 'desc' ? 'asc' : 'desc'
      this.config = { [field]: { order } }
      this.$emit('sort', { field, order })
    },

    sortIcon(field) {
      if (this.config[field]?.order) {
        return this.config[field].order === 'desc' ? 'sort-down' : 'sort-up'
      } else {
        return 'sort'
      }
    }
  }
}
</script>

<style lang="scss">
@import '../../styles/tokens/tokens.scss';

.nr-table-container {
  max-width: 100%;
  max-height: 100%;
  overflow-y: auto;
  overflow-x: auto;
  position: relative;
  .nr-table {
    border-collapse: separate;
    border-spacing: 0;
    margin: 0;
    padding: 0;
    position: relative;
    text-align: left;
    white-space: nowrap;
    width: 100%;
    .nr-table-sort-header {
      display: flex;
    }
    &.striped {
      tbody {
        tr:nth-child(odd) {
          &:hover {
            td {
              background-color: $color-purple-10;
            }
          }

          td {
            background-color: $color-purple-5;
          }
        }
      }
    }
    td,
    th {
      background-color: $color-white;
      border-bottom: $border-width-default solid $color-gray-20;
      padding: $spacing-8 $spacing-12;
      &.is-sortable:hover {
        border-color: $color-purple-80;
      }
    }
    th {
      position: sticky;
      text-align: left;
    }
    thead {
      th {
        top: 0;
        left: 0;
        z-index: 2;
        border-bottom-width: $border-width-medium;
        font-size: $font-size-3;
        font-weight: $font-weight-semibold;

        &.sticky {
          z-index: 3;
          clip-path: inset(0 -4px 0 0);
          box-shadow: 0 0 4px 0 rgba(black, 0.5);
        }
      }
    }
    tbody {
      a {
        color: $color-blue-100;
        font-weight: $font-weight-semibold;
      }
      th {
        border-color: transparent;
        box-shadow: 0 0 4px 0 rgba(black, 0.5);
        clip-path: inset(0 -4px 0 0);
        font-weight: $font-weight-regular;
        left: 0;
        z-index: 1;
      }
      tr {
        &:hover {
          td,
          th {
            background-color: $color-purple-10;
          }
        }
        .nr-icon {
          font-size: $font-size-5;
          &.is-blue {
            color: $color-blue-100;
          }
          &.is-yellow {
            color: $color-yellow-100;
          }
          &.is-red {
            color: $color-red-100;
          }
          &.is-black {
            color: $color-purple-110;
          }
          &.is-grey {
            color: $color-gray-20;
          }
        }
      }
    }
  }
  .nr-table-loading {
    bottom: 0;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    align-items: center;
    display: flex;
    justify-content: center;
    overflow: hidden;
    z-index: 29;
    .nr-table-overlay {
      background: $color-gray-40;
      opacity: $opacity-20;
      bottom: 0;
      left: 0;
      position: absolute;
      right: 0;
      top: 0;
    }
    .nr-table-spinner {
      align-items: center;
      display: inline-flex;
      justify-content: center;
      width: $size-medium;
      height: $size-medium;
    }
  }
  .nr-table-empty {
    text-align: center;
    font-weight: $font-weight-regular;
    .text {
      font-size: $font-size-3;
      font-weight: $font-weight-semibold;
      margin: $spacing-16;
    }
  }
}
</style>
